const SAFE_WEB_URL_PROTOCOLS: readonly string[] = ['http:', 'https:'];

/**
 * Whether a value is safe to open as a web link (window.open / href). The WHATWG URL parser strips
 * ASCII tab/newline before reading the scheme, so this also rejects `java\tscript:` bypasses that an
 * anchored `^javascript:` regex would miss. Mirrors the backend `is_safe_web_url()` helper.
 */
export const isSafeWebUrl = (url: string): boolean => {
  let parsedUrl: URL;
  try {
    parsedUrl = new URL(url);
  } catch (_) {
    return false;
  }

  return SAFE_WEB_URL_PROTOCOLS.includes(parsedUrl.protocol);
};

export const getCacheBustedURL = (url: string): string => {
  const urlObj = new URL(url);
  urlObj.searchParams.set('ts', new Date().getTime().toString());

  return urlObj.href;
};

export function getURISegment(segmentIndex = 0): string | null {
  const path = window.PMW.util.getCurrentPagePath().replace(/^\/(.*)/, '$1');
  const segments = path.split('/');
  const isValidIndex = segmentIndex < segments.length;

  return isValidIndex ? segments[segmentIndex] : null;
}

export const isValidURL = (urlToValidate: string): boolean => {
  if (urlToValidate.split(' ').length > 1) {
    return false;
  }

  const regex = /[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&/{2}=]*)/;
  return regex.test(urlToValidate);
};

export const isValidURL2 = (urlString: string): boolean => {
  if (!urlString) {
    return false;
  }

  let urlForValidation = urlString.trim();

  // Prepend "https://" if no protocol is present.
  // This allows the URL constructor to parse domain-only inputs like "google.com".
  if (!/^(https?:\/\/)/i.test(urlForValidation)) {
    urlForValidation = `https://${urlForValidation}`;
  }

  try {
    const url = new URL(urlForValidation);
    // A simple but effective check: a valid public hostname must contain a dot.
    // This correctly rejects single-word inputs like "google" or "http://google".
    return url.hostname.includes('.');
  } catch (e) {
    // The URL constructor will throw an error for malformed URLs.
    return false;
  }
};

export const isLikelyUrl = (value: string): boolean => {
  const val = value.trim();

  try {
    const parsed = new URL(val);
    if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
      return true;
    }
  } catch (_) {
    // not a full URL with scheme
  }

  // Check if it's a likely domain (e.g. google.com)
  const domainRegex = /^[A-Za-z0-9]([A-Za-z0-9-]{0,61}[A-Za-z0-9])?(\.[A-Za-z]{2,}){1,2}$/;
  return domainRegex.test(value);
};

export const cleanURIComponent = (uri: string): string => {
  return uri
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with dashes
    .replace(/-+/g, '-') // Remove duplicate dashes
    .replace(/^-+|-+$/g, ''); // Trim leading and trailing dashes
};

export const hasInvalidCharacters = (urlToValidate: string): boolean => {
  const pattern = /["<>#%{}|^~[\]`]/;
  return pattern.test(urlToValidate);
};

export const getFilenameFromURL = (url: string): string => {
  const splitted = url.split('/');
  return splitted.pop()!;
};

export const isValidClickableLink = (url: string): boolean => {
  return url === '' || (window.PMW.util.isValidURLSimple(url) as boolean) || (window.PMW.util.isValidPhoneSimple(url) as boolean);
};

export const prependProtocol = (url: string): string => {
  const hasExistingProtocol = /^(mailto:|tel:|https?:\/\/)/i.test(url);
  if (hasExistingProtocol) {
    return url;
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (emailRegex.test(url)) {
    return `mailto:${url}`;
  }

  if (window.PMW.util.isValidPhoneSimple(url)) {
    return `tel:${url}`;
  }

  return `https://${url}`;
};

export const getStateFromUrl = (url: string): string => {
  return url.split('state=')[1].split('&')[0];
};

export const doesTextContainGoogleMeetURL = (text: string): boolean => {
  const regex = /https:\/\/meet\.google\.com\/\S+/;
  const match = regex.exec(text);
  return !!match;
};

export const isGoogleMeetLink = (url: string): boolean => {
  let parsedUrl = null;

  try {
    parsedUrl = new URL(url);
  } catch (e) {
    return false;
  }
  const host = parsedUrl.hostname;
  return host === 'meet.google.com' || host.endsWith('.meet.google.com');
};

export const extractGoogleMeetURL = (text: string): string | undefined => {
  const regex = /https:\/\/meet\.google\.com\/\S+/;
  const match = regex.exec(text);
  return match ? match[0] : undefined;
};

export const isZoomLink = (url: string): boolean => {
  let parsedUrl = null;

  try {
    parsedUrl = new URL(url);
  } catch (e) {
    return false;
  }
  const host = parsedUrl.hostname;
  return /\.?zoom\.(us|com)$/.test(host);
};

export const isWebpURL = (url: string): boolean => {
  return url.includes('.webp');
};

export const convertWebpURLToJPG = (url: string): string => {
  if (isWebpURL(url)) {
    return url.replace(/\.webp$/, '.jpg');
  }

  return url;
};

export const isValidUrl = (url: string): boolean => {
  if (!url) {
    return true;
  }

  const pattern = /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;
  return pattern.test(url);
};

export const formatUrl = (url: string): string => {
  if (!url) return '';

  let formattedUrl = url.trim();
  if (formattedUrl && !formattedUrl.includes('://')) {
    formattedUrl = 'https://' + formattedUrl;
  }
  return formattedUrl;
};

export const getUrlHasHttps = (url: string): boolean => {
  return /^[a-zA-Z]+:\/\//.test(url);
};

export const doNormalizeURLForHttps = (url: string): string => {
  const hasHttps = getUrlHasHttps(url);
  if (hasHttps) {
    return url;
  }

  return addHttpsToUrl(url);
};

export const addHttpsToUrl = (url: string): string => {
  return `https://${url}`;
};
